/**
 * Short, non-blocking messages at the bottom of the screen. Announced to
 * screen readers through an aria-live region. Errors stay longer.
 */
import { esc } from '../dom';
import './ui.css';

export type ToastKind = 'info' | 'success' | 'error';

let region: HTMLElement | null = null;

function ensureRegion(): HTMLElement {
  if (region?.isConnected === true) return region;
  region = document.createElement('div');
  region.className = 'toasts';
  region.setAttribute('role', 'status');
  region.setAttribute('aria-live', 'polite');
  document.body.append(region);
  return region;
}

export function toast(message: string, kind: ToastKind = 'info', ms?: number): void {
  const el = document.createElement('div');
  el.className = `toast toast--${kind}`;
  el.innerHTML = esc(message);
  ensureRegion().append(el);
  const duration = ms ?? (kind === 'error' ? 6000 : 3500);
  window.setTimeout(() => {
    el.classList.add('toast--leaving');
    window.setTimeout(() => {
      el.remove();
    }, 400);
  }, duration);
}

/** Turn any thrown value into a German message and show it. */
export function toastError(err: unknown, fallback = 'Da ist etwas schiefgegangen.'): void {
  toast(errorMessage(err, fallback), 'error');
}

/**
 * The German reason of a thrown value: the server's `reason` (an API
 * refusal, e.g. "Zu diesem Unterthema gehören noch 40 Fragen."), the
 * first `issues` message, or the error's own text.
 */
export function errorMessage(err: unknown, fallback = 'Da ist etwas schiefgegangen.'): string {
  if (err instanceof Error && /^API \d+$/.test(err.message)) {
    const body = (err as Error & { body?: unknown }).body;
    if (typeof body === 'object' && body !== null) {
      const b = body as { reason?: unknown; issues?: { message?: unknown }[] };
      if (typeof b.reason === 'string' && b.reason !== '') return b.reason;
      const first = Array.isArray(b.issues) ? b.issues[0]?.message : undefined;
      if (typeof first === 'string' && first !== '') return first;
    }
    return fallback;
  }
  return err instanceof Error && err.message !== '' ? err.message : fallback;
}
